import { AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useTableLineage } from "~/pages/datasets/hooks/useTableLineage";
import { TableLineageGraph } from "./TableLineageGraph";

interface TableLineageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableFQN?: string;
  tableName?: string;
}

/**
 * Modal dialog for displaying table lineage visualization.
 *
 * Handles loading, error, and empty states. Passes data to TableLineageGraph
 * component for rendering.
 */
export const TableLineageModal = ({
  open,
  onOpenChange,
  tableFQN,
  tableName,
}: TableLineageModalProps) => {
  const { data, isLoading, error } = useTableLineage(open ? tableFQN : undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent fullscreen className="h-[94vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>Table Lineage</DialogTitle>
          <DialogDescription>
            {tableName
              ? `Viewing upstream and downstream dependencies for ${tableName}`
              : "Viewing table dependencies"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 relative">
          {isLoading && (
            <div
              data-testid="lineage-loading"
              className="absolute inset-0 flex items-center justify-center bg-white/80 z-10"
            >
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-gray-600">Loading lineage data...</p>
              </div>
            </div>
          )}

          {error && (
            <div
              data-testid="lineage-error"
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="flex flex-col items-center gap-3 text-center max-w-md px-4">
                <AlertCircle className="h-12 w-12 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Failed to load lineage data</p>
                  <p className="text-sm text-gray-500 mt-1">{error.message}</p>
                </div>
              </div>
            </div>
          )}

          {data && !isLoading && !error && tableFQN && (
            <div data-testid="lineage-graph" className="h-full w-full">
              <TableLineageGraph data={data} currentTableFQN={tableFQN} />
            </div>
          )}

          {!data && !isLoading && !error && (
            <div
              data-testid="lineage-empty"
              className="absolute inset-0 flex items-center justify-center"
            >
              <p className="text-sm text-gray-500">No table selected</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
